import { Box, Divider, Stack, Tab, Tabs } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ErrorBoundary from '../../ErrorBoundary';
import { usePersistedState } from '../../common/hooks/usePersistedState';
import { FormError } from '../../types/FormError';
import { LoadState } from '../../types/LoadState';
import { varderingToBostadsinformation } from '../lib/varderingToBostadsinformation';
import {
  BostadsinformationDto,
  BostadsvarderingDto,
} from '../types/dtos/VarderingarModel';
import { SavedVarderingar } from './SavedVarderingar/SavedVarderingar';
import { VarderingAdressForm } from './VarderingForm/VarderingAdressForm';
import { VarderingForm } from './VarderingForm/VarderingForm';
import { VarderingRapport } from './VarderingRapport/VarderingRapport';
import { VarderingSokLayout } from './VarderingSokLayout';
import { TabView } from './common/TabView';

enum View {
  Bostadsvardering = 'BOSTADSVARDERING',
  Historik = 'HISTORIK',
}

export const viewStorageKey = 'view-bostadsvardering';
export const activeBostadsvarderingStorageKey = 'active-bostadsvardering';

const initialView: View = View.Bostadsvardering;

interface VarderingPageProps {
  loadState: LoadState;
  error: FormError;
  setError: (value: FormError) => void;
  savedVarderingar: BostadsvarderingDto[];
  setSavedVarderingar: (value: BostadsvarderingDto[]) => void;
  getSavedVarderingar: (useLoadState?: boolean) => Promise<void>;
}

export const VarderingPage: React.FC<VarderingPageProps> = ({
  loadState,
  error,
  setError,
  savedVarderingar,
  setSavedVarderingar,
  getSavedVarderingar,
}) => {
  const [bostadsinformation, setBostadsinformation] =
    useState<BostadsinformationDto>();
  const [activeBostadsvardering, setActiveBostadsvardering] =
    usePersistedState<BostadsvarderingDto>(
      activeBostadsvarderingStorageKey,
      undefined,
      true
    );
  const [view, setView] = usePersistedState<View>(viewStorageKey, initialView);
  const varderingElement = useRef<HTMLDivElement>();

  useEffect(() => {
    getSavedVarderingar(false);
    if (activeBostadsvardering) {
      setBostadsinformation(
        varderingToBostadsinformation(activeBostadsvardering)
      );
      window.requestAnimationFrame(() =>
        varderingElement.current?.scrollIntoView(true)
      );
    }
  }, [activeBostadsvardering]);

  return (
    <ErrorBoundary onError={() => setActiveBostadsvardering(undefined)}>
      <Stack>
        <VarderingSokLayout
          heading="Bostadsvärdering"
          subheading="ett samarbete mellan Metria och Valueguard"
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={view} onChange={(_, t) => setView(t)}>
              <Tab label="Bostadsvärdering" value={View.Bostadsvardering} />
              <Tab label="Historik" value={View.Historik} />
            </Tabs>
          </Box>
          <TabView viewType={View.Bostadsvardering} selectedView={view}>
            <Stack divider={<Divider />} spacing={3} mt={2}>
              <VarderingAdressForm
                setBostadsinformation={setBostadsinformation}
                setActiveVardering={setActiveBostadsvardering}
              />
              <VarderingForm
                bostadInformation={bostadsinformation}
                activeVardering={activeBostadsvardering}
                setActiveVardering={setActiveBostadsvardering}
              />
            </Stack>
          </TabView>
          <TabView viewType={View.Historik} selectedView={view}>
            <SavedVarderingar
              activeBostadsvardering={activeBostadsvardering}
              setActiveBostadsvardering={setActiveBostadsvardering}
              savedVarderingar={savedVarderingar}
              setSavedVarderingar={setSavedVarderingar}
              loadState={loadState}
              error={error}
              setError={setError}
            />
          </TabView>
        </VarderingSokLayout>
        {activeBostadsvardering && (
          <VarderingRapport
            bostadsvardering={activeBostadsvardering}
            varderingElement={varderingElement}
          />
        )}
      </Stack>
    </ErrorBoundary>
  );
};
